import { createApi } from "@reduxjs/toolkit/query/react";
import { sharedBaseQuery } from "../../api/baseQuery";
import { Project } from "../../types/domain";

interface ProjectWithData extends Project {
  layouts?: any[];
  devices?: any[];
}

export const projectsApi = createApi({
  reducerPath: "projectsApi",
  baseQuery: sharedBaseQuery.baseQueryWithReauth,
  tagTypes: ["Project", "Layout", "Device"],
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], void>({
      query: () => ({ url: "projects" }),
      providesTags: ["Project"],
    }),
    getProject: builder.query<Project, number>({
      query: (id) => ({ url: `projects/${id}` }),
      providesTags: (_r, _e, id) => [{ type: "Project", id }],
    }),
    getProjectData: builder.query<ProjectWithData, number>({
      query: (id) => ({ url: `projects/${id}/data` }),
      providesTags: (_r, _e, id) => [
        { type: "Project", id },
        "Layout",
        "Device",
      ],
    }),
    getProjectLayouts: builder.query<any[], number>({
      query: (id) => ({ url: `projects/${id}/layouts` }),
      providesTags: ["Layout"],
    }),
    createProject: builder.mutation<Project, Partial<Project>>({
      query: (body) => ({ url: "projects", method: "POST", body }),
      invalidatesTags: ["Project"],
    }),
    updateProject: builder.mutation<
      Project,
      { id: number; body: Partial<Project> }
    >({
      query: ({ id, body }) => ({ url: `projects/${id}`, method: "PUT", body }),
      invalidatesTags: ["Project"],
    }),
    deleteProject: builder.mutation<void, number>({
      query: (id) => ({ url: `projects/${id}`, method: "DELETE" }),
      invalidatesTags: ["Project"],
    }),
    uploadProject: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "projects/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Project", "Layout", "Device"],
    }),
    addProjectLayout: builder.mutation<
      any,
      { projectId: number; formData: FormData }
    >({
      query: ({ projectId, formData }) => ({
        url: `projects/${projectId}/layouts`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Layout"],
    }),
    updateLayout: builder.mutation<any, { layoutId: number; body: any }>({
      query: ({ layoutId, body }) => ({
        url: `projects/layouts/${layoutId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Layout"],
    }),
    updateLayoutCalibration: builder.mutation<
      any,
      { layoutId: number; body: any }
    >({
      query: ({ layoutId, body }) => ({
        url: `projects/layouts/${layoutId}/calibration`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Layout"],
    }),
    deleteLayout: builder.mutation<void, number>({
      query: (layoutId) => ({
        url: `projects/layouts/${layoutId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Layout"],
    }),
    addDeviceToProject: builder.mutation<any, { projectId: number; body: any }>(
      {
        query: ({ projectId, body }) => ({
          url: `projects/${projectId}/devices`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Device"],
      },
    ),
    updateProjectDevice: builder.mutation<any, { deviceId: string; body: any }>(
      {
        query: ({ deviceId, body }) => ({
          url: `projects/devices/${deviceId}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["Device"],
      },
    ),
    deleteProjectDevice: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `projects/devices/${deviceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Device"],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useGetProjectDataQuery,
  useGetProjectLayoutsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useUploadProjectMutation,
  useAddProjectLayoutMutation,
  useUpdateLayoutMutation,
  useUpdateLayoutCalibrationMutation,
  useDeleteLayoutMutation,
  useAddDeviceToProjectMutation,
  useUpdateProjectDeviceMutation,
  useDeleteProjectDeviceMutation,
} = projectsApi;
